<template>
  <v-dialog v-model="dialog" persistent max-width="840px">
    <v-card id="signature_drawer">
      <v-card-title>
        {{ $t('signatureDrawer.title') }}
      </v-card-title>
      <v-card-text>
        <v-row align="center" justify="start" no-gutters>
          <v-col align-self="center">
            <v-menu :close-on-content-click="false" offset-y>
              <template #activator="{ on }">
                <v-btn class="mr-3" v-on="on">
                  <v-icon>mdi-pen</v-icon>
                  {{ $t('signatureDrawer.line') }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item>
                  <!-- Line Width Slider -->
                  <width-slider v-model="lineWidth" icon="mdi-pen" :max="50" :label="$t('signatureDrawer.lineWidth')" @input="handleValueChange"></width-slider>
                </v-list-item>
                <v-list-item>
                  <v-color-picker
                    v-model="lineColor" value="hex"
                    @input="handleValueChange"
                  ></v-color-picker>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-menu :close-on-content-click="false" offset-y>
              <template #activator="{ on }">
                <v-btn class="mr-3" v-on="on">
                  <v-icon>mdi-text-shadow</v-icon>
                  {{ $t('signatureDrawer.shadow') }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item>
                  <!-- Shadow Width Slider -->
                  <width-slider v-model="shadowWidth" icon="mdi-text-shadow" :label="$t('signatureDrawer.shadowWidth')" @input="handleValueChange"></width-slider>
                </v-list-item>
                <v-list-item>
                  <width-slider v-model="shadowOffset" :label="$t('signatureDrawer.shadowOffset')" @input="handleValueChange"></width-slider>
                </v-list-item>
                <v-list-item>
                  <v-color-picker
                    v-model="shadowColor" value="hex"
                    @input="handleValueChange"
                  ></v-color-picker>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Background Color Picker -->
            <color-picker v-if="backgroundColorSupported" v-model="backgroundColor" button-class="mr-3" icon="mdi-palette" :label="$t('signatureDrawer.backgroundColor')" @input="handleBackroundValueChange"></color-picker>

            <v-btn @click="clearCanvas">
              <v-icon>mdi-refresh</v-icon>
              {{ $t('signatureDrawer.clear') }}
            </v-btn>
          </v-col>
          <v-col align-self="center" cols="6">
            <width-slider v-model="canvasSize" icon="mdi-resize" :label="$t('signatureDrawer.canvasSize')" :min="1" :max="2" :step="0.1" @input="handleCanvasSizeChange"></width-slider>
          </v-col>
        </v-row>

        <v-row justify="center" align="center" class="pt-1 mt-1 ml-0 mr0 pa0" no-gutters>
          <v-col cols="12">
            <div class="canvasContainer" :style="{ width: canvasWidth * canvasSize + 'px', height: canvasHeight * canvasSize + 'px' }">
              <canvas id="signatureDrawer" ref="canvas" :width="canvasWidth * canvasSize" :height="canvasHeight * canvasSize">
              </canvas>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn
          outlined
          class="my-4"
          color="primary" @click="dialog = false"
        >
          <v-icon>mdi-close-circle-outline</v-icon>
          {{ $t('main.cancel') }}
        </v-btn>
        <v-btn
          id="save"
          color="primary"
          class="my-4"
          @click="saveCanvas"
        >
          <v-icon>mdi-content-save-outline</v-icon>
          {{ $t('main.ok') }}
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { fabric } from 'fabric'
import WidthSlider from '@/components/WidthSlider.vue'
import ColorPicker from '@/components/ColorPicker.vue'

export default {
  name: 'SignatureDrawer',
  /* props: {
    showDrawingCanvas: Boolean
  }, */
  components: {
    ColorPicker, WidthSlider
  },
  data() {
    return {
      dialog: false,
      fcanvas: null,
      dialogContentLoaded: false, // Flag to track if dialog content is loaded
      canvasHeight: 200,
      canvasWidth: 300,
      lineWidth: 2,
      backgroundColor: '#FFFFFF',
      lineColor: '#000000',
      shadowColor: '#B9B8B8',
      shadowWidth: 0,
      shadowOffset: 0,
      backgroundColorSupported: false,
      canvasSize: 1,
      brushOptions: {
        brushType: 'pencil',
        color: '#000000',
        lineWidth: 1,
        shadowWidth: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        shadowColor: '#000000'
      },
      showBrushSelector: false,
      brushTypes: ['pencil', 'circle', 'rect', 'square']
    }
  },
  async mounted() {
    // Watch for changes in the dialog visibility
    this.$watch('dialog', (newValue) => {
      if (newValue) {
        this.$nextTick(() => {
          this.dialogContentLoaded = true
          this.initPreview()
        })
      }
    })
  },
  methods: {
    saveCanvas() {
      const base64Data = this.fcanvas.toDataURL()
      this.dialog = false
      this.$emit('saved', base64Data)
    },
    clearCanvas() {
      this.fcanvas.clear()
    },
    handleCanvasSizeChange() {
      if (!this.fcanvas) {
        this.initPreview()
      }
      if (this.fcanvas) {
        this.fcanvas.setWidth(this.canvasWidth * this.canvasSize)
        this.fcanvas.setHeight(this.canvasHeight * this.canvasSize)

        this.fcanvas.renderAll()
      }
    },
    handleBackroundValueChange() {
      if (!this.fcanvas) {
        this.initPreview()
      }
      if (this.fcanvas) {
        this.fcanvas.backgroundColor = this.backgroundColor
        this.fcanvas.renderAll()
      }
    },
    handleValueChange() {
      if (!this.fcanvas) {
        this.initPreview()
      }
      if (this.fcanvas) {
        this.fcanvas.freeDrawingBrush.color = this.lineColor
        this.fcanvas.freeDrawingBrush.width = this.lineWidth
        if (!this.fcanvas.freeDrawingBrush.shadow) {
          this.fcanvas.freeDrawingBrush.shadow = new fabric.Shadow({
            blur: this.shadowWidth,
            offsetX: this.shadowOffset,
            offsetY: this.shadowOffset,
            affectStroke: true,
            color: this.shadowColor
          })
        } else {
          this.fcanvas.freeDrawingBrush.shadow.blur = this.shadowWidth
          this.fcanvas.freeDrawingBrush.shadow.color = this.shadowColor
          this.fcanvas.freeDrawingBrush.shadow.blur = this.shadowOffset
        }
      }
      // this.fcanvas.renderAll()
    },
    initPreview() {
      window.fabric.Object.prototype.objectCaching = false

      if (this.fcanvas) {
        this.fcanvas.remove(...this.fcanvas.getObjects())
        this.fcanvas.clear()
        this.fcanvas.dispose()
        this.fcanvas = null
      }

      if (!this.fcanvas) {
        this.fcanvas = new fabric.Canvas('signatureDrawer', {
          selection: false,
          isDrawingMode: true
        })
        this.fcanvas.set('backgroundColor', 'transparent')

        this.fcanvas.renderAll()
      }
    }
  }
}
</script>
<style scoped>
.canvas {
  border: 1px solid black;
  direction: ltr;
}

#signatureDrawer {
  border: 1px solid black;
  direction: ltr;
  user-select: none;
  cursor: crosshair;
}

.canvasContainer {
  margin-left: auto;
  margin-right: auto;
  display: block;
}
.canvas-container {
  margin-left: auto !important;
  margin-right: auto !important;
}
</style>
